import { createRoute, type ErrorComponentProps } from "@tanstack/react-router";
import { lazy, type ReactElement, Suspense, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { rootRoute } from "../../root-route";
import { rechargerUneFois } from "../rechargement";
import { RECOMPENSES_PATH } from "./recompenses-entree";

/**
 * La route de la page des recompenses de points (`/regie/reglages/recompenses`, lot QP). La page est livree a part (elle
 * porte ses formulaires et ses phrases) : le paquet d'entree, deja pres de la limite de 500 ko que Vite signale, ne la
 * charge qu'a l'ouverture, comme les ecrans des cartes (`cartes-regie/routes-cartes.tsx`). Ce fichier n'importe donc
 * **rien** de `recompenses.tsx`, `recompenses-api.ts` ni `recompenses-texts.ts` : seulement l'adresse
 * (`recompenses-entree.ts`) et l'`import()` de la page. `route.test.tsx` le verifie sur le paquet d'entree.
 */

export const RECOMPENSES_CHARGEMENT = "Chargement de la page des récompenses...";
export const RECOMPENSES_INDISPONIBLE =
  "La page des récompenses n'a pas pu être chargée. Recharge la page : Castellan a peut-être été mis à jour depuis l'ouverture de cet onglet.";
export const RECOMPENSES_RECHARGER = "Recharger la page";

/** Le fichier de la page n'est pas arrive : la seule erreur que `ErreurDesRecompenses` traite. */
class ChargementRecompensesEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement de la page des recompenses echoue", { cause });
    this.name = "ChargementRecompensesEchoue";
  }
}

const RecompensesPage = lazy(() =>
  import("./recompenses").then(
    (module) => ({ default: module.RecompensesPage }),
    (cause: unknown) => {
      throw new ChargementRecompensesEchoue(cause);
    },
  ),
);

const RECHARGER_LA_PAGE = (): void => window.location.reload();

/**
 * Le fichier de la page n'est pas arrive (le plus souvent l'app a ete mise a jour depuis l'ouverture de l'onglet, et les
 * noms de fichier ont change) : une phrase, un rechargement tout seul (une fois par session de l'onglet) et le bouton, qui
 * recharge toujours. `recharger` est la pour les tests ; la route n'en passe pas.
 */
export function RecompensesIndisponibles({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <section role="alert" className="flex flex-col items-start gap-3">
      <p>{RECOMPENSES_INDISPONIBLE}</p>
      <Button onClick={recharger}>{RECOMPENSES_RECHARGER}</Button>
    </section>
  );
}

/** Seule l'absence du fichier de la page se traite ici ; toute autre erreur suit le chemin d'erreur normal du routeur. */
function ErreurDesRecompenses({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementRecompensesEchoue)) throw error;
  return <RecompensesIndisponibles />;
}

const attente = (
  <p role="status" className="text-sm text-muted-foreground">
    {RECOMPENSES_CHARGEMENT}
  </p>
);

export const recompensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: RECOMPENSES_PATH,
  component: () => (
    <Suspense fallback={attente}>
      <RecompensesPage />
    </Suspense>
  ),
  errorComponent: ErreurDesRecompenses,
});
